// Device settings and preferences
export interface DeviceSettings {
  id: string; // deviceId
  displayName: string;
  restrictionMode: 'BLOCKLIST' | 'ALLOWLIST';
  newAppPolicy: 'ALLOW' | 'BLOCK' | 'LIMIT';
  newAppDefaultLimitMinutes: number;
  timezone?: string;
  notificationsEnabled: boolean;
  notificationTypes?: {
    blockEvents: boolean;
    limitReached: boolean;
    appInstalled: boolean;
    scheduleChange: boolean;
  };
  dataExportEnabled: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

// Settings form state
export interface SettingsFormState {
  displayName: string;
  restrictionMode: 'BLOCKLIST' | 'ALLOWLIST';
  newAppPolicy: 'ALLOW' | 'BLOCK' | 'LIMIT';
  newAppDefaultLimitMinutes: number;
  timezone: string;
  notificationsEnabled: boolean;
  notificationBlockEvents: boolean;
  notificationLimitReached: boolean;
  notificationAppInstalled: boolean;
  notificationScheduleChange: boolean;
}

// Available timezones
export const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Bangkok',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
];

// Helper: Get timezone display name
export function getTimezoneDisplayName(tz: string): string {
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'long',
    });
    const parts = formatter.formatToParts(date);
    const tzName = parts.find(p => p.type === 'timeZoneName')?.value || tz;
    return `${tz} (${tzName})`;
  } catch {
    return tz;
  }
}

// Helper: Get list of timezones with display names
export function getTimezoneList(): Array<{ value: string; display: string }> {
  return TIMEZONE_OPTIONS.map(tz => ({
    value: tz,
    display: getTimezoneDisplayName(tz),
  }));
}

// Helper: Validate restriction mode
export function isValidRestrictionMode(mode: any): mode is 'BLOCKLIST' | 'ALLOWLIST' {
  return mode === 'BLOCKLIST' || mode === 'ALLOWLIST';
}

// Helper: Validate new app policy
export function isValidNewAppPolicy(policy: any): policy is 'ALLOW' | 'BLOCK' | 'LIMIT' {
  return policy === 'ALLOW' || policy === 'BLOCK' || policy === 'LIMIT';
}

// Settings change event
export interface SettingsChangeEvent {
  field: keyof DeviceSettings;
  oldValue: any;
  newValue: any;
  timestamp: Date;
}
